import { Box, Stack } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IncompleteRecord } from '../../types';
import { useGetIncompleteRecords } from '../../query/resource-hooks/company';
import { IncompleteRecordsFilter } from './IncompleteRecordsFilter';
import { IncompleteRecordsList } from './IncompleteRecordsList';
import { simpleReportModalDataToQueryString } from '../attendance/reports/SimpleReportModal';

type IncompleteRecordsSectionProps = {
    userId?: number; // Optional: filter by specific user
    defaultDateRange?: Date[]; // Optional: override default date range
};

export const IncompleteRecordsSection: React.FC<IncompleteRecordsSectionProps> = ({ userId, defaultDateRange }) => {
    const navigate = useNavigate();

    // Date range state - use provided default or calculate this week
    const [selectedDates, setSelectedDates] = useState<Date[]>(() => {
        if (defaultDateRange && defaultDateRange.length === 2) {
            return defaultDateRange;
        }
        // Default to this week
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return [start, end];
    });

    const [page, setPage] = useState(1);
    const [allRecords, setAllRecords] = useState<IncompleteRecord[]>([]);

    // Query incomplete records
    const incompleteRecordsQuery = useGetIncompleteRecords(
        {
            dateFrom: selectedDates[0]?.toISOString() || '',
            dateTo: selectedDates[1]?.toISOString() || '',
            page: page,
            perPage: 10,
            ...(!!userId && { userId: userId }),
        },
        selectedDates.length === 2
    );

    // Accumulate records when new page loads
    useEffect(() => {
        if (incompleteRecordsQuery.data?.data) {
            if (page === 1) {
                // Reset on first page (new filter or refresh)
                setAllRecords(incompleteRecordsQuery.data.data);
            } else {
                // Append new page data
                setAllRecords((prev) => [...prev, ...incompleteRecordsQuery.data!.data]);
            }
        }
    }, [incompleteRecordsQuery.data, page]);

    // Reset page when dates change
    const dateFromStr = selectedDates[0]?.toISOString();
    const dateToStr = selectedDates[1]?.toISOString();

    useEffect(() => {
        setPage(1);
    }, [dateFromStr, dateToStr]);

    function handleRecordClick(record: IncompleteRecord) {
        // Create date range for the report - use the date of the incomplete record
        const recordDate = new Date(record.date);
        const dateFrom = new Date(recordDate);
        dateFrom.setHours(0, 0, 0, 0);
        const dateTo = new Date(recordDate);
        dateTo.setHours(23, 59, 59, 999);

        // Generate query string using the helper function
        const queryString = simpleReportModalDataToQueryString(dateFrom.toISOString(), dateTo.toISOString(), record.userId);

        // Navigate to reports page with query params
        navigate(`/attendance/reports?${queryString}`);
    }

    return (
        <Stack flexDir={{ base: 'column', lg: 'row' }} width={'100%'} spacing={4}>
            <Box order={{ base: 1, lg: 2 }} width={{ base: '100%', lg: '30%' }}>
                <IncompleteRecordsFilter
                    onDateChange={setSelectedDates}
                    selectedDates={selectedDates}
                    totalRecords={incompleteRecordsQuery.data?.meta.total}
                    position="sticky"
                    top="22px"
                />
            </Box>
            <Box order={{ base: 2, lg: 1 }} width={{ base: '100%', lg: '70%' }}>
                <IncompleteRecordsList
                    records={allRecords}
                    isLoading={incompleteRecordsQuery.isLoading && page === 1}
                    onRecordClick={handleRecordClick}
                    hasNextPage={
                        incompleteRecordsQuery.data
                            ? incompleteRecordsQuery.data.meta.current_page < incompleteRecordsQuery.data.meta.last_page
                            : false
                    }
                    onLoadMore={() => setPage((prev) => prev + 1)}
                    isLoadingMore={incompleteRecordsQuery.isLoading && page > 1}
                    error={incompleteRecordsQuery.error}
                    onRetry={() => incompleteRecordsQuery.refetch()}
                />
            </Box>
        </Stack>
    );
};
